/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
// Queries/notesQueries.tsx
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createNotes } from '../../Repositories';

const notesReq = createNotes();

// canonical key builder
export const notesKey = (
  applicationId: number | string | null,
  interviewId: number | string | null,
  parent_notes_id: number | string | null
) => ['notesGetQuery', applicationId ?? null, interviewId ?? null, parent_notes_id ?? null];
export function useNotesGetQuery(reqParams: Record<string, any> = {}, options: any = {}) {
  const { application = null, interview = null, parent_notes_id = null } = reqParams;

  return useQuery(
    notesKey(application, interview, parent_notes_id),
    () => notesReq.getAllNotes(reqParams),
    {
      staleTime: 0,
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      ...options,
    }
  );
}

export function useNotesPostQuery() {
  const queryClient = useQueryClient();

  return useMutation((args: any) => notesReq.postNote(args), {
    retry: false,

    onSuccess: (resp) => {
      if (!resp) {
        queryClient.invalidateQueries(['notesGetQuery']);
        queryClient.invalidateQueries(['taskGetQuery']);
        return;
      }

      const interviewId = resp.interview_id ?? null;

      const keys = [
        ['notesGetQuery'],
        ['notesRepliesQuery'],
        ['applicationsGetQueryById'],
        ['positionsKanbanGetQueryById'],
        ['interviewsGetQueryById'],
        ['applicationsGetQuery'], // 👈 this is your RESUME_SCREENING API
        ['taskGetQuery'],
      ];

      keys.forEach((key) => queryClient.invalidateQueries(key));

      if (interviewId) {
        queryClient.invalidateQueries(['interviewsGetQueryById']);
      }
    },
  });
}

/**
 * Get single note by id
 */
export function useNotesGetQueryById(reqId?: number | string, options?: any) {
  return useQuery(['notesGetQueryById', reqId], () => notesReq.getNoteById(reqId as any), {
    enabled: !!reqId,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    ...options,
  });
}

/**
 * Get replies for a given parent id.
 * Returns { data, meta } like getAllNotes; your component can use `.data` field.
 */
export function useNotesRepliesQuery(
  parentId?: number | string,
  reqParams?: Record<string, any>,
  options?: any
) {
  return useQuery(
    ['notesRepliesQuery', parentId, reqParams],
    () => notesReq.getRepliesByParentId(parentId as any, reqParams),
    {
      enabled: !!parentId,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      ...options,
    }
  );
}

/**
 * Update (put) note by id
 */
export function useNotesPutQueryById() {
  const queryClient = useQueryClient();
  return useMutation((args: any) => notesReq.putNoteById(args), {
    retry: false,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['taskGetQuery']);
      queryClient.invalidateQueries(['notesGetQuery']);
      queryClient.invalidateQueries(['notesGetQueryById', variables?.reqId]);
      queryClient.invalidateQueries(['notesRepliesQuery']);
      queryClient.invalidateQueries(['applicationsGetQueryById']);
      queryClient.invalidateQueries(['positionsKanbanGetQueryById']);
      queryClient.invalidateQueries(['interviewsGetQueryById']);
      queryClient.invalidateQueries(['applicationsGetQuery']);
    },
  });
}

/**
 * Delete note by id
 */
export function useNotesDeleteQueryById() {
  const queryClient = useQueryClient();
  return useMutation((args: any) => notesReq.deleteNoteById(args), {
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(['taskGetQuery']);
      queryClient.invalidateQueries(['notesGetQuery']);
      queryClient.invalidateQueries(['notesRepliesQuery']);
    },
  });
}

export function useNotesTaskGetQueryById(reqId?: number | string, options?: any) {
  return useQuery(
    ['applicationsOfferGetQueryById', reqId],
    () => notesReq.getNotesTaskById(reqId as any),
    {
      enabled: !!reqId,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      ...options,
    }
  );
}
